import { articlePath } from "../utils/articleUrls";
import { apiFetch, apiUrl, isSuccess } from "./apiClient";

// ================================================================================================
// ===== RDS SERVICES  =====
// ================================================================================================

// GETs JSON. Auth failures (and any `fallbackStatuses`) and network errors
// resolve `fallback`; other error statuses reject with an Error.
async function getJsonOrFallback(
  url,
  { fallback, errorMessage, networkErrorLog, fallbackStatuses = [401, 403] },
) {
  let response;
  try {
    response = await apiFetch(url);
  } catch (error) {
    console.error(networkErrorLog, error);
    return fallback;
  }
  if (isSuccess(response)) {
    return response.json();
  }
  if (fallbackStatuses.includes(response.status)) {
    return fallback;
  }
  throw new Error(`${errorMessage}: ${response.status}`);
}

export function getArticleById(articleId) {
  return getJsonOrFallback(
    apiUrl(`/articles/by-id/${encodeURIComponent(articleId)}`),
    {
      fallback: null,
      errorMessage: "Failed to get article",
      networkErrorLog: "Article fetch error:",
    },
  );
}

// Resolves an old /articles/<slug> link (from before article URLs carried the
// ID) to { article_id, url }, or null when no live article had that slug.
export function getArticleByLegacyUrl(slug) {
  return getJsonOrFallback(
    apiUrl(`/articles/by-legacy-url/${encodeURIComponent(slug)}`),
    {
      fallback: null,
      fallbackStatuses: [401, 403, 404],
      errorMessage: "Failed to resolve legacy article",
      networkErrorLog: "Legacy article lookup error:",
    },
  );
}

export function getArticlesByQuery(queriesParamsString) {
  return getJsonOrFallback(apiUrl(`/articles/q?${queriesParamsString}`), {
    fallback: { items: [], total: 0 },
    errorMessage: "Failed to get article",
    networkErrorLog: "Article fetch error:",
  });
}

export function getArticleTags() {
  return getJsonOrFallback(apiUrl("/articles/tags"), {
    fallback: [],
    errorMessage: "Failed to get article tags",
    networkErrorLog: "Article tags fetch error:",
  });
}

export function getArticleTagsWithCount(queryParamsString) {
  return getJsonOrFallback(
    apiUrl(`/articles/tags/recommended/all?${queryParamsString}`),
    {
      fallback: [],
      errorMessage: "Failed to get article tags",
      networkErrorLog: "Article tags fetch error:",
    },
  );
}

export function getRecommendedArticleTags(tags) {
  return getJsonOrFallback(
    apiUrl(`/articles/tags/recommended?tags=${encodeURIComponent(tags)}`),
    {
      fallback: [],
      errorMessage: "Failed to get recommended article tags",
      networkErrorLog: "Recommended article tags fetch error:",
    },
  );
}

// ================================================================================================
// Articles V2 Services - Using RDS
// ================================================================================================

const normalizeDate = (value) => {
  if (!value) {
    return "";
  }
  return value.includes("T") ? value : `${value.replace(" ", "T")}Z`;
};

const toVehicleResourceItem = (item) => ({
  id: String(item.article_id),
  article_id: item.article_id,
  title: item.title,
  description: item.description,
  thumbnail: item.thumbnail,
  categories: item.categories || [],
  createdDate: normalizeDate(item.created_date),
  updatedDate: normalizeDate(item.updated_date),
  isRecommended: false,
  recordType: null,
  lastPromotedArticleId: null,
  hideDownloadButton: item.hide_download ?? false,
  url: item.url ?? null,
  tags: item.tags || [],
});

export async function getVehicleResourcesArticlesRds(seriesId, modelYear) {
  const url = apiUrl(
    `/articles/q/vehicleResources?seriesId=${encodeURIComponent(seriesId)}&modelYear=${encodeURIComponent(modelYear)}`,
  );

  let status;
  try {
    const response = await apiFetch(url);
    status = response.status;
    if (isSuccess(response)) {
      const payload = await response.json();
      return { Items: (payload?.items || []).map(toVehicleResourceItem) };
    }
    if (status === 401 || status === 403) {
      return { Items: [] };
    }
  } catch (error) {
    console.error("Vehicle resources articles RDS fetch error:", error);
    return { Items: [] };
  }
  throw new Error(
    `Failed to get vehicle resources articles from RDS: ${status}`,
  );
}

export async function getArticlesV2(date, arrayTags, limit = 25, page = 1) {
  const params = new URLSearchParams();

  if (arrayTags && arrayTags.length > 0) {
    const tagsString = arrayTags
      .map((tag) => encodeURIComponent(tag))
      .join(",");
    params.append("tags", tagsString);
  }

  if (date) {
    params.append("date", date);
  }

  if (limit) {
    params.append("limit", String(limit));
  }

  if (page) {
    params.append("page", String(page));
  }

  return getJsonOrFallback(apiUrl(`/articles/q?${params.toString()}`), {
    fallback: { items: [], hasMore: false },
    errorMessage: "Failed to get articles by tags",
    networkErrorLog: "Articles by tags V2 fetch error:",
  });
}

/**
 * Fetches the delivery checklist article path for a given vehicle name and year.
 * Finds the article tagged with all of "Delivery Checklist", the vehicle name and
 * the year; when several match, the most recently updated one wins.
 * vehicleName comes from getVehicleData response's `series` field (e.g. "Camry",
 * "Toyota Crown") — vehicle tags use VAPI's display name, not the URL seriesId.
 * Returns the article's /articles/<id>/<slug> path or null if not found.
 */
export async function getDeliveryChecklistArticlePath(vehicleName, year) {
  const params = new URLSearchParams({
    tags: ["Delivery Checklist", vehicleName, String(year)].join(","),
    tagsmode: "and",
    sort: "updated",
    order: "desc",
    limit: "1",
  });

  try {
    const result = await getArticlesByQuery(params.toString());
    const article = result?.items?.[0];
    return article ? articlePath(article.article_id, article.url) : null;
  } catch (error) {
    console.error("Delivery checklist article fetch error:", error);
    return null;
  }
}
